import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { DocumentCollectionGateModal } from '../components/DocumentCollectionGateModal';
import { useCase } from '../contexts/CaseContext';
import { buildCaseKey } from '../hooks/useCaseKey';
import { chromeOwner } from '../lib/breadcrumbTrail';

// Inner shell for the tabbed case workspace. Mounted only on the four
// top-level case tabs (Lit Desk index, Case Issues, Pleadings, Document
// Collection landing); drilled-in workflow screens (Document Discovery
// review, Discovery Responses, Members, Collection set view) continue to
// render their own Header without the tab bar.
//
// Tabs are URL-driven — each one is its own route so deep links and the
// browser back button work without per-screen state.
//
// Document Collection is treated as a different-altitude destination: it
// holds unreviewed, pre-production materials that may be privileged, so
// it sits at the rightmost tab, uses a rose accent instead of the amber
// underline shared by the case-work tabs, and clicks transitioning *into*
// it from another tab are gated by a confirmation modal. Clicks from
// within the collection subtree (the landing itself, or any deeper
// collection screen) bypass the modal — the user is already in
// collection territory, so a re-prompt would be friction without
// informational value. Direct URL navigation (deep links, refreshes, the
// browser back button) is not gated — the modal only intercepts in-app
// tab clicks.
//
// Special case: the Document Collection landing keeps a SECOND drilled-in
// state at the SAME `/collection` URL — when ?collection=<productionId>
// or ?set=<setId> is present, the user has clicked into a specific
// collection and the screen swaps to set view. In that state the shell
// unmounts its chrome and lets the screen render its own Header with a
// back arrow + collection-name breadcrumb, mirroring the deeper
// /collection/sets/:setId route.
export function CaseWorkspaceShell() {
  const caseData = useCase();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCollectionGate, setShowCollectionGate] = useState(false);
  const caseKey = buildCaseKey(caseData);

  const collectionPath = `/cases/${caseKey}/collection`;

  const tabs = [
    { label: 'Lit Desk', path: `/cases/${caseKey}`, exact: true, variant: 'standard' as const },
    { label: 'Case Issues', path: `/cases/${caseKey}/issues`, exact: false, variant: 'standard' as const },
    { label: 'Pleadings', path: `/cases/${caseKey}/pleadings`, exact: false, variant: 'standard' as const },
    { label: 'Parties', path: `/cases/${caseKey}/parties`, exact: false, variant: 'standard' as const },
    { label: 'Document Collection', path: collectionPath, exact: false, variant: 'gated' as const },
  ];

  const isActive = (tab: { path: string; exact: boolean }) =>
    tab.exact
      ? location.pathname === tab.path
      : location.pathname === tab.path ||
        location.pathname.startsWith(tab.path + '/');

  // Reset the gate if the user navigates away while it's open (e.g. browser
  // back, programmatic redirect) — otherwise the modal would render over an
  // unrelated screen and Proceed would jump them somewhere they didn't ask
  // for.
  useEffect(() => {
    setShowCollectionGate(false);
  }, [location.pathname]);

  // Drilled-in detection: on the Document Collection tab, query params
  // alone (no path change) can flip the screen into set/collection-detail
  // view. When that happens, surrender chrome to the screen.
  const onCollectionLanding = location.pathname === collectionPath;
  const inCollectionSubtree =
    onCollectionLanding || location.pathname.startsWith(collectionPath + '/');
  if (chromeOwner(location.pathname, location.search, caseKey) === 'screen') {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-navy">
      {/* Case Issues pins the app header so its sticky toolbar strip lands
          right under it (IssuesScreen's APP_HEADER_HEIGHT). Other tabs keep
          their own sticky offsets at top:0, so they stay as they were. */}
      <div className={isActive(tabs[1]) ? 'sticky top-0 z-30' : undefined}>
        <Header caseName={caseData.case_name} />
      </div>

      {/* The min-h-screen root means the window has always owned scrolling.
          Keeping main as overflow:auto only made it a non-scrolling sticky
          scrollport, which prevented sticky descendants from sticking. */}
      <main id="main" tabIndex={-1} className="flex-1">
        <div className="max-w-7xl mx-auto px-8 pt-10">
          <h1 className="text-4xl font-serif text-white mb-8">{caseData.case_name}</h1>

          <div className="flex items-center gap-8 border-b border-navy-light">
            {tabs.map((tab) => {
              const active = isActive(tab);
              const gated = tab.variant === 'gated';
              const baseColor = gated
                ? active
                  ? 'text-rose-300'
                  : 'text-gray-400 hover:text-rose-300'
                : active
                  ? 'text-white'
                  : 'text-gray-400 hover:text-gray-300';
              const underlineColor = gated ? 'bg-rose-400' : 'bg-amber';
              const className = `relative pb-4 text-[15px] font-serif tracking-tight transition-colors ${baseColor}`;
              const indicator = active ? (
                <span className={`absolute -bottom-px left-0 right-0 h-0.5 ${underlineColor}`} />
              ) : null;

              if (gated) {
                // Render as <Link> (not <button>) so the tab keeps the
                // browser-native affordances the other three tabs have:
                // cmd/ctrl/middle-click → new tab, right-click → Open in
                // New Tab / Copy Link, hover URL preview, and the link
                // entry in screen-reader rotors. The gate only intercepts
                // a plain left-click; modifier-click is allowed through as
                // a deliberate "open in new tab" gesture (consistent with
                // direct-URL navigation, which also bypasses the gate).
                //
                // Three branches on plain left-click:
                //   - on the landing exactly → no-op (already here)
                //   - inside the collection subtree → let Link navigate
                //     back to the landing without the gate (the user is
                //     already in collection territory; re-prompting would
                //     be friction without informational value, and matches
                //     how the other tabs return to their own landing)
                //   - anywhere else → gate
                return (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    aria-haspopup="dialog"
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                      if (onCollectionLanding) {
                        e.preventDefault();
                        return;
                      }
                      if (inCollectionSubtree) return;
                      e.preventDefault();
                      setShowCollectionGate(true);
                    }}
                    className={className}
                  >
                    {tab.label}
                    {indicator}
                  </Link>
                );
              }

              return (
                <Link key={tab.path} to={tab.path} className={className}>
                  {tab.label}
                  {indicator}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="pt-2">
          <Outlet />
        </div>
      </main>

      <DocumentCollectionGateModal
        open={showCollectionGate}
        onConfirm={() => {
          setShowCollectionGate(false);
          navigate(collectionPath);
        }}
        onCancel={() => setShowCollectionGate(false)}
      />
    </div>
  );
}
